// Whether an email is shown as its sender designed it, or in the app's own colors.
import { useEffect, useMemo, useState } from "preact/hooks";
import { defuseHtml } from "./defuse-html";
import { withoutHtmlQuote } from "./quote-html";

// Backgrounds that don't make a design: mail apps (Outlook especially) set white everywhere.
const PLAIN_BACKGROUND =
  /^(#fff(fff)?|white|transparent|none|inherit|initial|unset|rgba?\(\s*255\s*,\s*255\s*,\s*255\s*(,\s*1(\.0+)?\s*)?\)|rgba\([^)]*,\s*0(\.0+)?\s*\))$/i;

const channels = (value: string) => {
  const hex = value.match(/^#([\da-f]{3,4}|[\da-f]{6}|[\da-f]{8})$/i)?.[1];
  if (hex) {
    const digits = hex.length <= 4 ? [...hex].map((digit) => digit + digit) : hex.match(/../g);
    return (digits ?? []).slice(0, 3).map((pair) => Number.parseInt(pair, 16));
  }
  const rgb = value.match(/^rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)/i);
  return rgb ? rgb.slice(1, 4).map(Number) : [];
};

const nearWhite = (value: string) => {
  const rgb = channels(value);
  return rgb.length === 3 && rgb.every((channel) => channel > 240);
};

/** True when the HTML paints its own backgrounds, so it is shown as designed, on white. */
export const hasOwnDesign = (html: string) =>
  [...html.matchAll(/background(?:-color)?\s*:\s*([^;"'}]+)|bgcolor\s*=\s*["']?([^"'\s>]+)/gi)]
    .map((match) => (match[1] ?? match[2] ?? "").replace(/!important/i, "").trim())
    .some((value) => !PLAIN_BACKGROUND.test(value) && !nearWhite(value));

/** A quoted newsletter should not force a plain reply onto a white canvas. */
export const hasOwnReplyDesign = (html: string) => hasOwnDesign(withoutHtmlQuote(defuseHtml(html)));

/**
 * Whether a message takes the app's own colors (plain mail does, so dark themes don't show a
 * white box), judged on what it shows: `unlocked` content (a decrypted PGP message), else its own.
 * That reads the whole email, so it runs once per message, not on every render (each image that
 * loads resizes the frame, which renders it again).
 */
export const useThemed = (
  message: { id: string; html: string | null },
  unlocked: { html: string | null } | null,
) =>
  useMemo(() => {
    const html = (unlocked ?? message).html;
    return html === null || !hasOwnReplyDesign(html);
  }, [message.id, unlocked]);

/**
 * Changes whenever the app's look does (theme, light or dark, accent, theme editor tokens), so
 * a message frame that copied the theme's colors can copy them again.
 */
export const useLook = () => {
  const [look, setLook] = useState(0);
  useEffect(() => {
    const changed = () => setLook((value) => value + 1);
    // Theme stylesheets (uploaded themes, a theme made in the editor) live in <head>; a page
    // title changing there is not a new look.
    const styling = (node: Node | null) =>
      node?.nodeName === "STYLE" ||
      node?.nodeName === "LINK" ||
      node?.parentNode?.nodeName === "STYLE";
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        const nodes = [mutation.target, ...mutation.addedNodes, ...mutation.removedNodes];
        if (mutation.target === document.documentElement || nodes.some(styling)) {
          // A stylesheet that is still loading changes the look again once it arrives.
          for (const node of [...mutation.addedNodes, mutation.target])
            if (node.nodeName === "LINK") node.addEventListener("load", changed, { once: true });
          changed();
          return;
        }
      }
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: [
        "data-theme",
        "data-scheme",
        "data-accent",
        "data-color-mode",
        "data-contrast",
        "data-transparency",
        "style",
      ],
    });
    observer.observe(document.head, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ["href"],
    });
    return () => observer.disconnect();
  }, []);
  return look;
};
